import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Breadcrumb,
  BreadcrumbItem,
  Button,
  Badge,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Spinner,
} from 'reactstrap';
import classnames from 'classnames';
import dayjs from 'dayjs';
import { ToastContainer } from 'react-toastify';
import {
  IMPROVEMENT_STATUS_META,
  CLARIFICATION_STATUS_META,
} from '../../utils/improvementClarificationHelpers';
import useImprovementClarificationDetail from '../../hooks/useImprovementClarificationDetail';
import useProcurementDetail from '../../../../shared/offer-domain/procurement/useProcurementDetail';
import { useQuantityDeliveries } from '../../../../Components/QuantityTable';
import {
  getVisibleProcurementClauses,
  useClarificationHasUnread,
  useMarkClarificationRespondsAsRead,
} from '../../../../shared/offer-domain/respond';
import { RESPOND_LIST_PAGE_SIZE } from '../../../../shared/offer-domain/respond/listParams';
import useOfferMitraIdentity from '../../hooks/useOfferMitraIdentity';
import { useSubmittedBidder } from '../../te-bridge';
import Improvement from './Improvement';
import Clarification from './Clarification';

const DetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(1);
  const [visitedTabs, setVisitedTabs] = useState({ 1: true });
  const [readSynced, setReadSynced] = useState(false);
  const [localClarificationProgress, setLocalClarificationProgress] =
    useState(null);
  const {
    statusImprovement: statusImprovementFromState,
    statusClarification: statusClarificationFromState,
    statusClarificationProgress: statusClarificationProgressFromState,
    procurementMitraId: seedProcurementMitraId,
    idOffer: seedIdOffer,
  } = location.state || {};

  const { item, isLoading: isLoadingItem } =
    useImprovementClarificationDetail(id);

  const identitySeed = useMemo(
    () => ({
      seedProcurementMitraId:
        seedProcurementMitraId ?? item?.procurementMitraId ?? null,
      seedIdOffer: seedIdOffer ?? item?.idOffer ?? null,
    }),
    [
      seedProcurementMitraId,
      seedIdOffer,
      item?.procurementMitraId,
      item?.idOffer,
    ]
  );

  const { procurement, isLoading: isLoadingProcurement } =
    useProcurementDetail(id);
  const { parcels, isLoading: isLoadingParcels } = useQuantityDeliveries(id);
  const {
    procurementMitraId: hookMitraId,
    idOffer: hookIdOffer,
    isLoading: isLoadingIdentity,
  } = useOfferMitraIdentity(procurement, identitySeed);
  const { items: submittedBidders } = useSubmittedBidder(id, {
    enabled: Boolean(id),
    pageSize: RESPOND_LIST_PAGE_SIZE,
  });

  const listedMitraIds = useMemo(
    () =>
      (procurement?.procurementMitra ?? [])
        .filter(entry => entry?.id && entry.isDeleted !== true)
        .map(entry => entry.id),
    [procurement]
  );

  const procurementMitraId = useMemo(() => {
    if (identitySeed.seedProcurementMitraId) {
      return identitySeed.seedProcurementMitraId;
    }
    if (submittedBidders.length === 1 && submittedBidders[0]?.id) {
      return submittedBidders[0].id;
    }
    if (listedMitraIds.length === 1) {
      return listedMitraIds[0];
    }
    if (identitySeed.seedIdOffer) {
      const byOffer = submittedBidders.find(
        row => String(row.idOffer) === String(identitySeed.seedIdOffer)
      );
      if (byOffer?.id) return byOffer.id;
    }
    if (hookMitraId) return hookMitraId;
    if (hookIdOffer) {
      const byHookOffer = submittedBidders.find(
        row => String(row.idOffer) === String(hookIdOffer)
      );
      if (byHookOffer?.id) return byHookOffer.id;
    }
    return null;
  }, [
    identitySeed.seedProcurementMitraId,
    identitySeed.seedIdOffer,
    submittedBidders,
    listedMitraIds,
    hookMitraId,
    hookIdOffer,
  ]);

  const idOffer = useMemo(() => {
    if (identitySeed.seedIdOffer) return identitySeed.seedIdOffer;
    if (hookIdOffer) return hookIdOffer;
    const row = submittedBidders.find(
      bidder => String(bidder.id) === String(procurementMitraId)
    );
    return row?.idOffer ?? null;
  }, [
    identitySeed.seedIdOffer,
    hookIdOffer,
    submittedBidders,
    procurementMitraId,
  ]);
  const clauseDetailIds = useMemo(
    () =>
      getVisibleProcurementClauses(procurement)
        .map(clause => clause.id)
        .filter(Boolean),
    [procurement]
  );

  useEffect(() => {
    setReadSynced(false);
    setLocalClarificationProgress(null);
  }, [id, procurementMitraId]);

  const { hasUnread: hasClarificationUnread, clearUnread } =
    useClarificationHasUnread({
      procurementId: id,
      procurementMitraId,
      clauseDetailIds,
      isEvaluatorView: false,
      enabled: Boolean(id && procurementMitraId && !readSynced),
    });

  useMarkClarificationRespondsAsRead({
    procurementId: id,
    procurementMitraId,
    clauseDetailIds,
    isEvaluatorView: false,
    enabled: Boolean(id && procurementMitraId && activeTab === 2),
    waitForClauseIds: true,
    onComplete: () => {
      clearUnread();
      setReadSynced(true);
    },
  });

  const mitraClarificationProgress = useMemo(() => {
    if (!procurementMitraId) return null;
    const match = submittedBidders.find(
      row => String(row.id) === String(procurementMitraId)
    );
    return match?.statusClarificationProgress ?? null;
  }, [procurementMitraId, submittedBidders]);

  const title = item?.title || procurement?.titleProcurement || '-';
  const procurementNumber =
    item?.idProc || procurement?.procurementNumber || '-';
  const statusImprovementLabel =
    statusImprovementFromState || item?.statusImprovement;
  const statusClarificationLabel =
    item?.statusClarification || statusClarificationFromState;
  const statusClarificationProgress =
    localClarificationProgress ??
    mitraClarificationProgress ??
    statusClarificationProgressFromState ??
    null;
  const isLoading = isLoadingItem || isLoadingProcurement;

  useEffect(() => {
    setVisitedTabs(prev =>
      prev[activeTab] ? prev : { ...prev, [activeTab]: true }
    );
  }, [activeTab]);

  const toggleTab = tab => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  document.title = 'Improvement Clarification Detail';

  return (
    <React.Fragment>
      <ToastContainer closeButton={false} />
      <div className='page-content'>
        <Container fluid>
          <div className='email-wrapper d-lg-flex gap-1 mx-n4 mt-n4 p-1'>
            <div className='email-content minimal-border'>
              <div className='p-4 pb-0'>
                <div className='border-bottom border-bottom-dashed'>
                  <Row className='mt-n2 mb-1 mb-sm-0'>
                    <div className='col-sm order-3 order-sm-2'>
                      <div className='hstack gap-sm-1 align-items-center flex-wrap email-topbar-link'>
                        <h5 className='card-title mb-0 flex-grow-1'>
                          <Breadcrumb listClassName='p-3 py-2 bg-light'>
                            <BreadcrumbItem>
                              <Link to='/dashboard'>Home</Link>
                            </BreadcrumbItem>
                            <BreadcrumbItem>
                              <Link to='/apps-improvementclarification/view'>
                                Improvement Clarification
                              </Link>
                            </BreadcrumbItem>
                            <BreadcrumbItem active>
                              Detail #{procurementNumber || '-'}
                            </BreadcrumbItem>
                          </Breadcrumb>
                        </h5>
                      </div>
                    </div>
                  </Row>
                  <Row className='mb-4'>
                    <Col xs={12}>
                      <Row className='align-items-center g-3'>
                        <Col xs={12} lg>
                          <div className='d-flex align-items-start gap-3'>
                            <Button
                              color='light'
                              className='border shadow-sm rounded-circle d-flex align-items-center justify-content-center p-0 flex-shrink-0'
                              style={{ width: '2.5rem', height: '2.5rem' }}
                              onClick={() =>
                                navigate('/apps-improvementclarification/view')
                              }
                            >
                              <i className='bx bx-arrow-back fs-5 text-dark'></i>
                            </Button>
                            <div>
                              <h4 className='mb-2 fw-bold text-dark'>
                                {isLoading ? (
                                  <span className='d-inline-flex align-items-center gap-2'>
                                    <Spinner size='sm' color='primary' />
                                    <span className='text-muted fs-6 fw-normal'>
                                      Loading detail...
                                    </span>
                                  </span>
                                ) : (
                                  title
                                )}
                              </h4>
                              <div className='d-flex flex-wrap align-items-center gap-2 text-muted small'>
                                <i className='bx bx-hash'></i>
                                <span>Procurement ID: {procurementNumber}</span>

                                <i className='bx bx-calendar'></i>
                                <span>
                                  {item?.updatedDate || item?.createdDate
                                    ? dayjs(
                                        item.updatedDate || item.createdDate
                                      ).format('DD MMM YYYY')
                                    : 'N/A'}
                                </span>

                                {statusImprovementLabel && (
                                  <Badge
                                    color={
                                      IMPROVEMENT_STATUS_META[
                                        statusImprovementLabel
                                      ]?.color || 'secondary'
                                    }
                                    className='px-2 py-1 fs-12 fw-medium rounded-pill'
                                  >
                                    Improvement: {statusImprovementLabel}
                                  </Badge>
                                )}

                                {statusClarificationLabel && (
                                  <Badge
                                    color={
                                      CLARIFICATION_STATUS_META[
                                        statusClarificationLabel
                                      ]?.color || 'secondary'
                                    }
                                    className='px-2 py-1 fs-12 fw-medium rounded-pill'
                                  >
                                    Clarification: {statusClarificationLabel}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                        </Col>
                      </Row>
                      <hr className='mt-3 mb-0 border-secondary opacity-10' />
                    </Col>
                    <Col xs={12}>
                      <div className='message-list-content mx-n4 px-4 message-list-scroll'>
                        <Nav
                          tabs
                          className='nav-tabs-custom nav-primary mt-3 mb-0'
                        >
                          <NavItem>
                            <NavLink
                              className={classnames('cursor-pointer', {
                                active: activeTab === 1,
                              })}
                              onClick={() => toggleTab(1)}
                            >
                              <i className='ri-flashlight-line align-middle me-1'></i>
                              <span className='fw-semibold'>Improvement</span>
                            </NavLink>
                          </NavItem>
                          <NavItem>
                            <NavLink
                              className={classnames('cursor-pointer', {
                                active: activeTab === 2,
                              })}
                              onClick={() => toggleTab(2)}
                            >
                              <i className='ri-question-answer-line align-middle me-1'></i>
                              <span
                                className={
                                  hasClarificationUnread
                                    ? 'fw-semibold text-info'
                                    : 'fw-semibold'
                                }
                              >
                                Clarification
                              </span>
                            </NavLink>
                          </NavItem>
                        </Nav>

                        <TabContent activeTab={activeTab} className='pt-3'>
                          {visitedTabs[1] && (
                            <TabPane tabId={1}>
                              <Improvement
                                procurement={procurement}
                                item={item}
                                procId={id}
                                parcels={parcels}
                                isLoadingParcels={isLoadingParcels}
                                isLoadingProcurement={isLoadingProcurement}
                                idOffer={idOffer}
                                isLoadingIdentity={isLoadingIdentity}
                              />
                            </TabPane>
                          )}
                          {visitedTabs[2] && (
                            <TabPane tabId={2}>
                              <Clarification
                                procurementId={id}
                                procurement={procurement}
                                isLoadingProcurement={isLoadingProcurement}
                                statusClarificationProgress={
                                  statusClarificationProgress
                                }
                                onProgressChange={setLocalClarificationProgress}
                                procurementMitraId={procurementMitraId}
                                idOffer={idOffer}
                                isLoadingIdentity={isLoadingIdentity}
                              />
                            </TabPane>
                          )}
                        </TabContent>
                      </div>
                    </Col>
                  </Row>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default DetailPage;
